import { useMemo, useState } from "react";

import type { EventCategoryDto, UserDto } from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { DateRange } from "@/components/DateRange/DateRange";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CalendarIcon } from "@/components/Icons/CalendarIcon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { MarkerPin04Icon } from "@/components/Icons/MarkerPin04Icon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Tool02Icon } from "@/components/Icons/Tool02Icon";
import { User01Icon } from "@/components/Icons/User01Icon";
import { AppUserItem } from "@/components/Phone/AppCommon";
import { PhoneInner } from "@/components/Phone/PhoneInner";
import { Badge } from "@/design-system/Badge";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { getEventCategoryName } from "@/helpers/events";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";
import { RuntimeColorBadge, RuntimeColorContent } from "@/ui-adapters/RuntimeColorBadge";

export interface AppEventDetailsViewProps {
  name: string;
  description: string;
  location: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  participantCount: number;
  maxParticipants?: number;
  image?: string;
  iconChar?: string;
  isAnnouncement?: boolean;
  category?: EventCategoryDto;
  projectConnection?: string;
  noParticipantsLimit?: boolean;
  organizer: UserDto;
  projectTintColor?: string;
  isEditMode?: boolean;
}
export function AppEventDetailsView({
  name,
  description,
  image,
  maxParticipants = 0,
  location,
  startDate,
  endDate,
  participantCount,
  isAnnouncement,
  category,
  projectConnection,
  noParticipantsLimit,
  organizer,
  projectTintColor,
  isEditMode,
}: AppEventDetailsViewProps): React.ReactNode {
  const [previewDate] = useState(() => new Date());
  const { i18n, t } = useTranslation();
  const availableSpotsAmount = useMemo(() => {
    const value = maxParticipants - participantCount;
    if (value <= 0) {
      return 0;
    }

    return value;
  }, [maxParticipants, participantCount]);

  return (
    <PhoneInner>
      <RuntimeBackgroundImage imageUrl={image || bgPlaceholder} presentation="banner" />
      <section className="relative bg-background-default px-space-sm pt-space-sm pb-space-xs">
        <Stack gap="sm">
          {category?.id === "maintenance" ? (
            <Badge icon={<Tool02Icon />}>{getEventCategoryName(t, category.id)}</Badge>
          ) : null}
          {projectConnection ? <Badge icon={<MarkerPin04Icon />}>{projectConnection}</Badge> : null}
        </Stack>
        <p className="my-space-sm flex items-center gap-space-xs text-text-primary">
          <CalendarIcon size={12} />
          {startDate && endDate ? <DateRange start={startDate.toISOString()} end={endDate.toISOString()} /> : "-"}
        </p>
        <Spread space="md">
          <p className="line-clamp-3 text-caption-emphasis break-all">{name || "-"}</p>
          {!isAnnouncement && (availableSpotsAmount > 0 || noParticipantsLimit) && (
            <RuntimeColorBadge color={projectTintColor || "var(--color-text-brand-secondary)"}>
              <PlusIcon size={12} />
              {t("model.event.join")}
            </RuntimeColorBadge>
          )}
        </Spread>

        {isEditMode ? (
          <p className="my-space-sm line-clamp-3 text-caption-emphasis break-all text-text-secondary uppercase">
            {t("model.event.edited")}
          </p>
        ) : null}

        {location ? (
          <p className="my-space-sm flex items-center gap-space-xs text-text-primary">
            <RuntimeColorContent color={projectTintColor || "var(--color-text-brand-secondary)"}>
              <MarkerPin01Icon size={12} />
            </RuntimeColorContent>
            <span>{location}</span>
          </p>
        ) : null}

        <p className="my-space-sm flex items-center gap-space-xs text-text-primary">
          <RuntimeColorContent color={projectTintColor || "var(--color-text-brand-secondary)"}>
            <User01Icon size={12} />
          </RuntimeColorContent>
          <span>{organizer.fullName}</span>
        </p>

        {!isAnnouncement && availableSpotsAmount > 0 && (
          <p className="my-space-sm text-text-primary">
            {t("model.event.spots.spots-available", {
              count: availableSpotsAmount,
            })}
          </p>
        )}

        <p className="my-space-md break-all whitespace-pre-line">{description || "-"}</p>

        {isEditMode ? (
          <p className="my-space-sm line-clamp-3 text-label-xs break-all text-text-secondary">
            {t("model.event.last-edit", {
              date: formatDate(i18n, "datetime", previewDate),
            })}
          </p>
        ) : null}
      </section>
      {!isAnnouncement && availableSpotsAmount > 0 && (
        <>
          <h3 className="bg-aop-off-white px-space-sm pt-space-md pb-space-sm text-body-emphasis text-text-primary uppercase">
            {t("model.event.participant-count", {
              count: availableSpotsAmount,
            })}
          </h3>
          <section className="bg-aop-off-white">
            <ul className="flex flex-col gap-0.5">
              {Array.from(
                {
                  length: Math.min(availableSpotsAmount, NUMBER_OF_PARTICIPANTS),
                },
                () => undefined,
              ).map((_, i) => (
                <AppUserItem
                  key={i}
                  fullName={t("model.event.preview-participant.name")}
                  subTitle={t("model.event.preview-participant.address")}
                />
              ))}
            </ul>
          </section>
        </>
      )}
    </PhoneInner>
  );
}
const NUMBER_OF_PARTICIPANTS = 4;
